import { Badge } from "@/components/ui/badge";
import { formatDateHumanReadable } from "@/lib/date-utils";
import { htmlFromString, isHTML } from "@/lib/string-util";
import type { Notification } from "@/model/notification";
import { NotificationActionButton } from "./notification-detail";

const NotificationCard = ({
  notification,
  onSelect,
}: {
  notification: Notification;
  onSelect?: (notification: Notification) => void;
}) => {
  const renderShortSecureMessage = (msg: string) => {
    if (isHTML(msg)) {
      const shortSecureMsg = msg
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/\s{2,}/g, " "); // replace(/<[^>]*>/g, ' '): replace <></> tags with single space, replace(/&nbsp;/g, ' '): replace '&nbsp;' with single space, replace(/\s{2,}/g, ' '): replace two space with single
      if (shortSecureMsg.length > 400) {
        return shortSecureMsg.substring(0, 400);
      } else {
        return shortSecureMsg;
      }
    } else {
      if (msg.length > 400) {
        return msg.substring(0, 400);
      } else {
        return msg;
      }
    }
  };

  // console.log('notification data >', notification)
  return (
    /*
      2.1.1 Keyboard — A. The card-level click is a pointer-only convenience: the same
      action is available to keyboard users through the subject button below, and the card
      cannot itself become a <button> because it already contains one
      (NotificationActionButton) — nesting interactive elements is invalid. Marked
      presentational so it is not announced as an extra, unreachable control.
    */
    <div
      role="presentation"
      className={`mb-4 flex items-center justify-between gap-10 rounded border p-4 ${!notification.isRead ? "bg-primary-extraLight" : "bg-white"} hover:bg-slate-100`}
      onClick={() => onSelect?.(notification)}
    >
      <div>
        {/*Badge Container*/}
        <div className="mb-2 flex flex-col items-start gap-2 md:flex-row md:items-center">
          {/* 2.1.1 Keyboard — A: these carried `onClick={() => {}}` — dead no-op handlers
              on a non-interactive element. Removing them drops a fake affordance rather
              than adding keyboard support for something that never did anything. */}
          {notification?.isSimulated ? (
            <Badge variant="default" className="bg-claim-processing">
              SIMULATED
            </Badge>
          ) : null}
          {notification?.isRead === false ? (
            <Badge variant="default">New</Badge>
          ) : null}
        </div>
        <div
          className={`min-w-0 flex-1 ${notification.isRead === false ? "" : "opacity-70"}`}
        >
          <p className="text-sm font-normal leading-none">
            {formatDateHumanReadable(notification?.createdTime)}
          </p>
          {/*
            2.1.1 Keyboard — A. The card itself is a <div onClick>, which no keyboard user
            can reach. The card cannot simply become a <button> because it already contains
            one (NotificationActionButton), and nesting interactive elements is invalid.
            The standard resolution is a real button on the primary action — the subject —
            so the same functionality is keyboard-operable, while the card-level click
            remains a pointer-only convenience. stopPropagation prevents onSelect firing
            twice when the button itself is clicked.
          */}
          <h3 className="font-semibold">
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onSelect?.(notification);
              }}
              className="text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {notification?.subject
                ? htmlFromString(notification?.subject)
                : "Notification"}
            </button>
          </h3>
          {/*
            1.3.1 Info and Relationships — A. This was a <Title>, which at the time always
            rendered an <h1> — so every notification in the list contributed a level-one
            heading whose text was the message preview, wrecking the document outline.
            Title now takes an explicit level, but this is body text, so it is a paragraph.
            (It also carried a `sixe="xsm"` typo, so the intended size never applied; the
            equivalent classes are inlined here instead.)
          */}
          <p className="line-clamp-2 overflow-hidden text-ellipsis text-sm font-normal leading-normal">
            {notification?.shortSecureMessage
              ? renderShortSecureMessage(notification?.shortSecureMessage)
              : ""}
          </p>
        </div>
      </div>
      {notification?.actionButton?.length > 0 ? (
        <div
          onClick={(e) => {
            e.stopPropagation(); //this prevents the card's onclick from firing
          }}
        >
          <NotificationActionButton notification={notification} limit={1} />
        </div>
      ) : null}
    </div>
  );
};

export default NotificationCard;
